import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { permissions } from "../../../permissions";
import { getLocalStorageItem } from "../../Catalog";
import { useGetPurchaseRequestListings } from "../../../hooks/get/useGetPurchaseRequestListings";
import config from "@surgia/config";

// Components
import { GenericTableHeader } from "@surgia/ui";
import TableControls from "@surgia/ui/containers/table/table-controls";
import PurchaseRequestTable from "../../../containers/tables/purchaseRequest-table";
import PurchaseRequestDetailsModal from "../../../containers/Modals/PurchaseRequestDetailsModal";

// Constants
const ITEMS_PER_PAGE = 8;

function PurchaseRequest() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { id: branchId } = useParams();

  // Get user permissions from global state
  const userPermissions = useSelector(
    (state: RootState) =>
      state.auth.globalState.user_permissions?.map(
        (permission) => permission.name
      ) || []
  );

  // Get tenant ID from localStorage
  const tenant_id = getLocalStorageItem("tenant_id", "");

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [status, setStatus] = useState<string | undefined>("SUBMITTED");
  const [search, setSearch] = useState("");
  const [selectedPRForDetails, setSelectedPRForDetails] = useState<{
    prId: string | null;
    branchId: string | null;
  } | null>(null);

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  const statusTabs = [
    { label: "Submitted", value: "SUBMITTED" },
    { label: "Drafts", value: "DRAFT" },
    { label: "History", value: "ORDERED" },
  ];

  // Use the hook to fetch purchase request listings with sorting
  const { data: purchaseRequestListings, isLoading } =
    useGetPurchaseRequestListings({
      componentId: String(config.componentId),
      tenantId: tenant_id || "",
      branchesIds: branchId || "",
      status: status as "SUBMITTED" | "DRAFT" | "ORDERED",
      page: currentPage,
      size: ITEMS_PER_PAGE,
      has_purchase_order: false,
      // Add sorting parameters
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    });

  useEffect(() => {
    setCurrentPage(0);
  }, [search, status]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const handleViewPRDetails = (prId: string, branchId: string) => {
    setSelectedPRForDetails({ prId, branchId });
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
        <GenericTableHeader
          title="Purchase Requests"
          totalCount={purchaseRequestListings?.total || 0}
          status={status}
          singularLabel="request"
          pluralLabel="requests"
        />

        <TableControls
          status={status}
          setStatus={setStatus}
          search={search}
          setSearch={setSearch}
          openFilters={false}
          setOpenFilters={() => {}}
          statusTabs={statusTabs}
          showFilter={false}
          showSearch={false}
        />

        {/* Purchase Requests Table Section */}
        <PurchaseRequestTable
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          data={purchaseRequestListings?.data || []}
          totalPages={Math.ceil(
            (purchaseRequestListings?.total || 0) / ITEMS_PER_PAGE
          )}
          loading={isLoading}
          showBranchColumn={false} // Don't show branch column since we're already in branch details
          selectedPurchaseRequests={[]} // No selection needed in branch details
          setSelectedPurchaseRequests={() => {}} // No selection needed in branch details
          status={status}
          onViewDetails={handleViewPRDetails}
          sortState={sortState}
          onSort={handleSort}
          hasViewPermission={userPermissions.includes(
            permissions.VIEW_DRAFT_PURCHASE_REQUEST
          )}
          hasEditDraftPOPermission={false} // No edit permissions in branch details
        />
      </div>

      {/* PR Details Modal */}
      <PurchaseRequestDetailsModal
        open={!!selectedPRForDetails}
        onClose={() => setSelectedPRForDetails(null)}
        purchaseRequestId={selectedPRForDetails?.prId || ""}
        branchId={selectedPRForDetails?.branchId || ""}
        componentId={String(config.componentId)}
        tenantId={tenant_id || ""}
      />
    </div>
  );
}

export default PurchaseRequest;
